/**
 * CheckoutSuccess -- Stripe success_url lands here with `?session_id=...`.
 *
 * Behavior:
 *   * Poll `GET /api/stripe/status/:session_id` until the payment
 *     transaction becomes SUCCEEDED (or EXPIRED). Max 8 polls at 1.5s.
 *   * Show a friendly confirmation once the status resolves.
 *   * If the webhook is faster than the user lands here, the first poll
 *     will already show SUCCEEDED -- no latency penalty.
 */
import * as React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CircleCheckBig, PackageSearch } from "lucide-react";
import { api, extractApiError } from "../../lib/api";
import { Card, CardBody } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";

type Phase = "polling" | "success" | "expired" | "error" | "unauthenticated";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");

  const [phase, setPhase] = React.useState<Phase>("polling");
  const [attempts, setAttempts] = React.useState(0);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!sessionId) {
      setPhase("error");
      setError("Missing session_id in URL.");
      return;
    }
    let cancelled = false;
    let count = 0;
    const poll = async () => {
      if (cancelled) return;
      try {
        const r = await api.get(`/stripe/status/${sessionId}`);
        if (cancelled) return;
        const status = r.data?.status;
        if (status === "SUCCEEDED") {
          setPhase("success");
          return;
        }
        if (status === "EXPIRED") {
          setPhase("expired");
          return;
        }
        count += 1;
        setAttempts(count);
        if (count >= 8) {
          setPhase("expired");
          return;
        }
        setTimeout(poll, 1500);
      } catch (err: any) {
        if (cancelled) return;
        // The webhook is the source of truth -- a 401 here just means the
        // session cookie didn't flow back yet. Route to an explicit
        // "sign in to view your order" state rather than a red error.
        const code = err?.response?.status;
        if (code === 401 || code === 403) {
          setPhase("unauthenticated");
          return;
        }
        if (code === 404) {
          // Session not yet recorded in our DB -- keep polling.
          count += 1;
          setAttempts(count);
          if (count >= 8) {
            setPhase("expired");
            return;
          }
          setTimeout(poll, 1500);
          return;
        }
        setError(extractApiError(err));
        setPhase("error");
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="mx-auto max-w-xl px-4 py-12 md:py-20">
      <Card elevated data-testid="checkout-success-card">
        <CardBody className="text-center py-10">
          {phase === "polling" && (
            <>
              <div className="h-12 w-12 rounded-full border-4 border-surface-border border-t-brand animate-spin mx-auto mb-5" />
              <h1 className="text-2xl font-black mb-2">Confirming your payment</h1>
              <p className="text-ink-muted">
                Waiting on Stripe… {attempts > 0 && `(${attempts}/8)`}
              </p>
            </>
          )}
          {phase === "success" && (
            <>
              <CircleCheckBig className="h-14 w-14 text-brand mx-auto mb-5" aria-hidden />
              <h1 className="text-3xl font-black mb-3" data-testid="checkout-success-heading">
                Payment received
              </h1>
              <p className="text-ink-muted mb-6">
                Thanks! Your order is confirmed. The seller has been notified
                and will ship it shortly.
              </p>
              <div className="flex flex-col gap-3">
                <Link to="/profile">
                  <Button variant="primary" size="lg" fullWidth>
                    View my orders
                  </Button>
                </Link>
                <Link to="/">
                  <Button variant="outline" size="lg" fullWidth leadingIcon={<PackageSearch className="h-5 w-5" />}>
                    Keep shopping
                  </Button>
                </Link>
              </div>
            </>
          )}
          {phase === "expired" && (
            <>
              <h1 className="text-2xl font-black mb-3">Still processing</h1>
              <p className="text-ink-muted mb-6">
                Stripe is taking longer than usual. You can check your order
                history in a minute — we won’t double-charge you.
              </p>
              <Link to="/profile">
                <Button variant="primary" size="lg" fullWidth>My orders</Button>
              </Link>
            </>
          )}
          {phase === "unauthenticated" && (
            <>
              <h1 className="text-2xl font-black mb-3" data-testid="checkout-unauth-heading">
                Please sign in to view your order
              </h1>
              <p className="text-ink-muted mb-6">
                Your payment has been received by Stripe. Sign in to see the
                order details and confirmation.
              </p>
              <Link to={`/login?redirect=/checkout/success?session_id=${sessionId ?? ""}`}>
                <Button variant="primary" size="lg" fullWidth data-testid="checkout-unauth-login">
                  Sign in
                </Button>
              </Link>
            </>
          )}
          {phase === "error" && (
            <>
              <h1 className="text-2xl font-black mb-3 text-brand">Something went wrong</h1>
              <p className="text-ink-muted mb-6">{error ?? "Please try again."}</p>
              <Link to="/">
                <Button variant="outline" size="lg" fullWidth>Back to marketplace</Button>
              </Link>
            </>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
